import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CheckCircleIcon,
  CheckIcon,
  LockClosedIcon,
} from "@heroicons/react/solid";

import { RiskMitigation } from "components/Risk/types";
import { TrackingContext } from "context/Tracking";
import { useAllTranslatedStandards } from "hooks/useStandards";

import { BuyStandardModal } from "./BuyStandardModal";
import {
  findStandardTitle,
  getGroundedStandards,
  getUngroundedStandards,
} from "./standardsAccess";

// DIN Media corporate identity color.
const DIN_BLUE = "#003f64";

interface StandardsAccessPanelProps {
  mitigations: RiskMitigation[];
  // Triggers the editor's existing regeneration flow once new standards are
  // unlocked. Optional — the re-analyse CTA only shows when provided.
  onRegenerate?: () => void;
}

// Read-only upsell panel: lists the applied standards the AI could NOT ground
// on (the customer doesn't own them yet) and offers a mocked DIN Media purchase
// per standard. Renders nothing when everything is grounded.
export const StandardsAccessPanel = ({
  mitigations,
  onRegenerate,
}: StandardsAccessPanelProps) => {
  const { t } = useTranslation();
  const { track } = useContext(TrackingContext);
  const { translations } = useAllTranslatedStandards();

  const ungrounded = useMemo(
    () => getUngroundedStandards(mitigations),
    [mitigations]
  );
  const grounded = useMemo(
    () => getGroundedStandards(mitigations),
    [mitigations]
  );

  const [purchased, setPurchased] = useState<string[]>([]);
  const [selected, setSelected] = useState<string | null>(null);

  if (!ungrounded.length) {
    return null;
  }

  const openBuy = (standard: string) => {
    setSelected(standard);
    track("ai risk: standard buy opened", { standard });
  };

  return (
    <div
      className="rounded-lg border border-gray-200 bg-white overflow-hidden border-l-4"
      style={{ borderLeftColor: DIN_BLUE }}
    >
      <div className="px-4 py-3">
        <p className="text-sm font-semibold text-gray-900">
          {t("risks.ai.standards.access.headline")}
        </p>
        <p className="mt-1 text-sm text-gray-600">
          {t("risks.ai.standards.access.explanation")}
        </p>
        <p className="mt-2 text-sm font-medium text-gray-800">
          {t("risks.ai.standards.access.valueProp")}
        </p>

        <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
          {t("risks.ai.standards.access.missing.label")}
        </p>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ungrounded.map((standard) => {
            const isOwned = purchased.includes(standard);
            const title = findStandardTitle(translations, standard);

            return (
              <div
                key={standard}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 shadow-sm flex items-center justify-between gap-3"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <LockClosedIcon
                    className={`h-4 w-4 flex-shrink-0 ${
                      isOwned ? "text-green-500" : ""
                    }`}
                    style={isOwned ? undefined : { color: DIN_BLUE }}
                    aria-hidden="true"
                  />
                  <span className="min-w-0">
                    <span className="block text-xs font-medium text-gray-800 truncate">
                      {standard}
                    </span>
                    {title && (
                      <span className="block text-xs text-gray-500 truncate">
                        {title}
                      </span>
                    )}
                  </span>
                </span>

                {isOwned ? (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 flex-shrink-0">
                    <CheckIcon className="h-4 w-4" aria-hidden="true" />
                    {t("risks.ai.standards.access.owned")}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => openBuy(standard)}
                    style={{ backgroundColor: DIN_BLUE }}
                    className="inline-flex justify-center py-1.5 px-3 border border-transparent shadow-sm text-xs font-medium rounded-md text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 flex-shrink-0"
                  >
                    {t("risks.ai.standards.access.buy.cta")}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {grounded.length > 0 && (
          <>
            <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t("risks.ai.standards.access.grounded.label")}
            </p>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {grounded.map((standard) => {
                const title = findStandardTitle(translations, standard);

                return (
                  <div
                    key={standard}
                    className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 flex items-center gap-2"
                  >
                    <CheckCircleIcon
                      className="h-4 w-4 flex-shrink-0 text-green-500"
                      aria-hidden="true"
                    />
                    <span className="min-w-0">
                      <span className="block text-xs font-medium text-gray-800 truncate">
                        {standard}
                      </span>
                      {title ? (
                        <span className="block text-xs text-gray-500 truncate">
                          {title}
                        </span>
                      ) : (
                        <span className="block text-xs text-gray-500 truncate">
                          {t("risks.ai.standards.access.grounded.tag")}
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {purchased.length > 0 && onRegenerate && (
          <div className="mt-4 border-t border-gray-100 pt-4">
            <p className="text-sm text-gray-600">
              {t("risks.ai.standards.access.regenerate.hint")}
            </p>
            <button
              type="button"
              onClick={onRegenerate}
              style={{ backgroundColor: DIN_BLUE }}
              className="mt-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-xs font-medium rounded-md text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2"
            >
              {t("risks.ai.standards.access.regenerate.cta")}
            </button>
          </div>
        )}
      </div>

      <BuyStandardModal
        show={selected !== null}
        standard={selected}
        title={selected ? findStandardTitle(translations, selected) : undefined}
        onClose={() => setSelected(null)}
        onPurchased={(standard) => {
          setPurchased((prev) =>
            prev.includes(standard) ? prev : [...prev, standard]
          );
          track("ai risk: standard purchased", { standard });
        }}
      />
    </div>
  );
};
